import { motion } from "framer-motion";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";
import { CtaSection } from "@/components/CtaSection";

const SERVICES = [
  {
    n: "01",
    title: "Growth Strategy",
    desc: "Before a dollar goes to ads, we get the strategy right. We dig into your numbers, your market and your message, then build a roadmap that ties every channel to revenue.",
    bullets: [
      "Marketing audits",
      "Positioning & messaging",
      "Channel strategy & roadmap",
      "KPI definition & tracking plan",
    ],
    who: "For brands that are spending — or about to spend — and want a plan before they scale.",
  },
  {
    n: "02",
    title: "Paid Ads & Performance",
    desc: "Full-funnel paid media managed by people who read numbers, not screenshots. We launch, test and optimize relentlessly until the economics work — then we scale what wins.",
    bullets: [
      "Meta (Facebook/Instagram) ads",
      "Google Ads (Search, YouTube, PMax)",
      "Creative testing frameworks",
      "Landing page & funnel optimization",
      "Weekly reporting & optimization calls",
    ],
    who: "For brands ready to turn ad spend into predictable growth.",
  },
  {
    n: "03",
    title: "Creative & Content",
    desc: "Creative is the lever that moves performance. We produce the ads, video, copy and content that make people stop scrolling, feel something, and buy.",
    bullets: [
      "Ad creatives (static + video concepts)",
      "Content strategy for social & email",
      "Copywriting for ads, landing pages, emails",
      "Brand voice & messaging guidelines",
    ],
    who: "For brands whose ads look fine but don't convert.",
  },
  {
    n: "04",
    title: "Marketing Systems & Automation",
    desc: "Growth leaks when systems are missing. We build the CRM, email/SMS and reporting infrastructure that captures every lead and compounds every win.",
    bullets: [
      "CRM setup & integration",
      "Email/SMS flows (welcome, abandon cart, post-purchase)",
      "Lead capture & nurture systems",
      "Basic analytics dashboards",
    ],
    who: "For brands drowning in manual follow-up and missed leads.",
  },
];

export default function WhatWeDo() {
  return (
    <>
      <Seo
        title="What We Do — Covelli"
        siteName="Covelli"
        description="Full-stack marketing built around growth, not vanity metrics: strategy, paid ads, creative and marketing systems."
      />
      <section data-testid="what-we-do-hero" className="mx-auto max-w-7xl px-6 pb-8 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand"
        >
          Services
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          What We Do
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          Full-stack marketing built around growth, not vanity metrics.
        </motion.p>
      </section>

      <div className="mx-auto max-w-7xl px-6 pb-24">
        {SERVICES.map((s, i) => (
          <motion.section
            key={s.n}
            data-testid={`service-section-${s.n}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`grid gap-8 border-t border-slate-200 py-14 md:grid-cols-2 md:gap-16 md:py-20 ${
              i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
            }`}
          >
            <div>
              <span className="font-mono text-xs font-semibold tracking-[0.25em] text-kinetic">{s.n}</span>
              <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl md:text-4xl">
                {s.title}
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600">{s.desc}</p>
              <p className="mt-7 border-l-2 border-skybrand pl-4 text-sm leading-relaxed text-slate-500">
                <span className="font-mono text-[11px] uppercase tracking-widest text-skybrand">
                  Who this is for
                </span>
                <br />
                {s.who}
              </p>
            </div>
            <ul className="space-y-4 self-center rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              {s.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-base text-slate-700">
                  <Check size={17} className="mt-1 shrink-0 text-skybrand" />
                  {b}
                </li>
              ))}
            </ul>
          </motion.section>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="border-t border-slate-200 py-14"
        >
          <h2 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">
            How we work with you
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Audit and strategy first, then build and launch, then optimize and
            scale — with systems and automation layered in so results compound.
            You get regular calls, plain-language reporting, and a team that
            treats your budget like its own.
          </p>
        </motion.div>
      </div>

      <CtaSection
        testid="what-we-do-cta"
        headline="Not sure what you need? We'll help you choose."
        subtext="One call. We'll map your situation to the right starting point — even if that isn't us."
        buttonText="Book a Strategy Call"
      />
    </>
  );
}
